import React, { useEffect, useMemo, useRef } from 'react';
import { View, Text, Pressable, Platform } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Animated, { useAnimatedStyle, useSharedValue, withSpring, withTiming } from 'react-native-reanimated';
import { useKeyboardState } from 'react-native-keyboard-controller';
import MessageInput from '@/components/Connect/Conversations/MessageInput';
import Unicon from '@/components/General/Unicon';
import useMessagesStore from '@/data/store/useMessagesStore';
import usePlatform from '@/utils/hooks/usePlatform';
import { decodeMentionsForEditing, stripMentions } from '@/utils/mentionFormat';

const SPRING = { damping: 24, stiffness: 300, mass: 0.8 };

/**
 * EditContainer — floating edit UI that appears above the keyboard when the user
 * taps "Edit" on their own message. Mirrors ReplyContainer. Prefills the
 * MessageInput with the message text (mention tokens decoded to labels) and
 * calls onEditMessage with the new text + attachments.
 *
 * Props:
 *   conversationID: string
 *   members: array (for mention detection)
 *   bottomInset: number
 *   placeholder: string
 *   onEditMessage: (text, attachments) => void
 */
const EditContainer = ({
    conversationID,
    members,
    bottomInset = 0,
    placeholder = "Edit message...",
    onEditMessage,
}) => {
    const { isWeb } = usePlatform();

    const editingMessage = useMessagesStore((state) => state.editingMessage);
    const setEditingMessage = useMessagesStore((state) => state.setEditingMessage);

    const keyboardVisible = useKeyboardState((state) => state.isVisible);

    const inputRef = useRef(null);
    const keyboardWasVisible = useRef(false);
    const hasEdit = !!editingMessage;

    //Decode mention tokens → display labels + mentions list for prefill.
    //Memoized on the message id so it only runs once when an edit starts.
    const { displayText, mentions: initialMentions } = useMemo(() => {
        if(!editingMessage?.text) return { displayText: '', mentions: [] };
        return decodeMentionsForEditing(editingMessage.text);
    }, [editingMessage?.id]);

    //Focus the input when edit starts — small delay lets the TextInput mount.
    useEffect(() => {
        if (editingMessage) {
            keyboardWasVisible.current = false;
            const timer = setTimeout(() => {
                inputRef.current?.focus();
            }, 250);
            return () => clearTimeout(timer);
        }
    }, [editingMessage]);

    //Track when keyboard becomes visible during an edit
    useEffect(() => {
        if (keyboardVisible && editingMessage) {
            keyboardWasVisible.current = true;
        }
    }, [keyboardVisible, editingMessage]);

    //Only clear edit state if keyboard was visible and then closed
    useEffect(() => {
        if (!keyboardVisible && editingMessage && keyboardWasVisible.current) {
            setEditingMessage(null);
        }
    }, [keyboardVisible, editingMessage]);

    //Animate in/out — slide up + fade in when edit starts, slide down + fade out when cleared.
    const opacity = useSharedValue(0);
    const slideY = useSharedValue(60);

    useEffect(() => {
        if (hasEdit) {
            opacity.value = withTiming(1, { duration: 200 });
            slideY.value = withSpring(0, SPRING);
        } else {
            opacity.value = withTiming(0, { duration: 150 });
            slideY.value = withSpring(60, SPRING);
        }
    }, [hasEdit]);

    const containerStyle = useAnimatedStyle(() => ({
        opacity: opacity.value,
        transform: [{ translateY: slideY.value }],
    }));

    //Don't render content when no edit — saves the MessageInput from doing work
    if (!hasEdit) return null;

    return (
        <Animated.View
            testID="edit-container"
            style={[styles.container, !isWeb && containerStyle]}
        >
            {/* Edit preview header */}
            <View style={styles.editHeader}>
                <View style={styles.editHeaderLeft}>
                    <Unicon name="create-outline" size={16} colorVariant="textSecondary" />
                    <Text style={styles.editingLabel}>Editing</Text>
                </View>
                <Pressable testID="edit-container-close" style={styles.closeButton} onPress={() => setEditingMessage(null)}>
                    <Unicon name="close" size={18} colorVariant="textSecondary" />
                </Pressable>
            </View>

            <View style={styles.editPreview}>
                <Text numberOfLines={2} style={styles.editPreviewText}>
                    {stripMentions(editingMessage?.text) || (editingMessage?.attachments?.length > 0 ? "Attachment" : "")}
                </Text>
            </View>

            {/* Embedded MessageInput — prefilled with the message text + mentions */}
            <MessageInput
                inputRef={inputRef}
                hideReplyBanner
                style={styles.embeddedInput}
                detection={["mention", "link"]}
                members={members}
                placeholder={placeholder}
                bottomInset={bottomInset}
                attachmentDirectory={"messageAttachments/" + conversationID}
                initialText={displayText}
                initialMentions={initialMentions}
                onSendMessage={(text, attachments) => {
                    onEditMessage(text, attachments);
                    setEditingMessage(null);
                }}
            />
        </Animated.View>
    );
};

const styles = StyleSheet.create((theme) => ({
    container: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 60,
        backgroundColor: theme.colors.background,
        borderTopWidth: 1,
        borderTopColor: theme.colors.border,
        ...(Platform.OS !== 'web' ? {
            shadowColor: '#000',
            shadowOffset: { width: 0, height: -4 },
            shadowOpacity: 0.1,
            shadowRadius: 8,
            elevation: 8,
        } : {}),
    },
    editHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 16,
        paddingTop: 10,
        paddingBottom: 4,
    },
    editHeaderLeft: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
    },
    editingLabel: {
        color: theme.colors.textSecondary,
        fontSize: 12,
        fontFamily: theme.fonts.heading,
    },
    closeButton: {
        padding: 4,
    },
    editPreview: {
        paddingHorizontal: 16,
        paddingBottom: 8,
    },
    editPreviewText: {
        color: theme.colors.textSecondary,
        fontSize: 12,
        lineHeight: 16,
    },
    embeddedInput: {
        position: 'relative',
        bottom: 'auto',
        left: 'auto',
        right: 'auto',
        width: '100%',
        paddingBottom: 5,
    },
}));

export default React.memo(EditContainer);
